import { useCallback, useEffect, useMemo, useState } from "react";
import { getApiBase } from "../apiBase";
import "./WillPreparationPanel.css";

const API_BASE = getApiBase();

const WILL_PREP_COPY = {
  en: {
    title: "Will Preparation Toolkit",
    intro:
      "Organize draft planning notes, family context, executor preferences, assets, liabilities, and digital legacy details for lawyer review.",
    contextAria: "Toolkit context",
    planningDocuments: "Planning and documents",
    disclaimerTitle: "Not legal advice",
    disclaimerPoints: [
      "AfterMe does not provide legal, tax, medical, or financial advice.",
      "Do not enter passwords, seed phrases, private keys, SIN, or full account numbers.",
      "You may choose to request lawyer review as a paid next step later.",
    ],
    reviewRequested: "Review requested",
    readyForReview: "Ready for review",
    draftInProgress: "Draft in progress",
    stepperAria: "Will preparation steps",
    legalNoticeTitle: "Before you continue",
    legalNoticeDescription:
      "Use this space to collect information and draft planning notes before a lawyer meeting. It is not a final or legally valid will.",
    important: "Important:",
    legalImportantBody:
      "Final documents may require province-specific legal formalities, review, signing, witnessing, and other professional steps.",
    legalChecklist: [
      "AfterMe.ca does not provide legal advice.",
      "Information entered here is for organization and lawyer-preparation only.",
      "Do not enter passwords, private keys, seed phrases, full account numbers, SIN, or highly sensitive credentials.",
      "You may choose to request lawyer review later if that service becomes available.",
    ],
    legalAcknowledge:
      "I understand this is a preparation tool and not a legally valid will.",
    savingAcknowledgement: "Saving acknowledgement…",
    continue: "Continue",
    summaryTitle: "Preparation summary",
    summaryDescription:
      "Use this summary to see what is complete and what may still need a lawyer conversation.",
    complete: "Complete",
    sectionsDone: "Sections done",
    requiredGaps: "Required gaps",
    riskFlags: "Risk flags",
    completedSections: "Completed sections",
    stillMissing: "Still missing",
    noRequiredGaps: "No required gaps right now.",
    preparingSummary: "Preparing summary…",
    downloadSummary: "Download preparation summary",
    refreshing: "Refreshing…",
    refreshSummary: "Refresh summary",
    lawyerReviewTitle: "Lawyer review next step",
    lawyerReviewDescription:
      "You can use this draft to prepare for a lawyer meeting. Any lawyer fees are separate, and nothing is finalized until review, signing, witnessing, and provincial legal requirements are completed.",
    requestReviewHeading: "Request lawyer review",
    requestReviewBody:
      "Start a request if you want AfterMe.ca to hand this preparation summary to a lawyer or review team later.",
    currentStatus: "Current status:",
    stillInDraft: "Still in draft",
    reviewRequestSent: "Review request sent",
    requestLawyerReview: "Request lawyer review",
    back: "Back",
    saveDraft: "Save draft",
    saving: "Saving…",
    reviewSummary: "Review summary",
    loadFailed: "Could not load the Will Preparation Toolkit.",
    modalKicker: "Lawyer review request",
    modalTitle: "Start lawyer review request",
    modalClose: "Close",
    modalLead:
      "This will start a request only. Legal fees may apply later, and no lawyer-client relationship is created until accepted by the lawyer or service provider.",
    modalConsent:
      "I allow AfterMe.ca to share this preparation summary with the lawyer or review team.",
    modalMessageLabel: "Optional message for the review team",
    modalMessagePlaceholder:
      "Example: I want help turning this draft into a lawyer-ready checklist.",
    modalNotNow: "Not now",
    modalSending: "Sending request…",
    modalStart: "Start lawyer review request",
  },
  fr: {
    title: "Trousse de préparation testamentaire",
    intro:
      "Organisez des notes préparatoires, le contexte familial, les préférences d’exécuteur, les actifs, les dettes et les détails de patrimoine numérique pour une révision par un avocat.",
    contextAria: "Contexte de la trousse",
    planningDocuments: "Planification et documents",
    disclaimerTitle: "Pas un avis juridique",
    disclaimerPoints: [
      "AfterMe ne fournit pas de conseils juridiques, fiscaux, médicaux ou financiers.",
      "N’entrez pas de mots de passe, phrases de récupération, clés privées, NAS ou numéros de compte complets.",
      "Vous pourrez plus tard demander une révision par un avocat comme prochaine étape payante.",
    ],
    reviewRequested: "Révision demandée",
    readyForReview: "Prêt pour la révision",
    draftInProgress: "Brouillon en cours",
    stepperAria: "Étapes de préparation testamentaire",
    legalNoticeTitle: "Avant de continuer",
    legalNoticeDescription:
      "Utilisez cet espace pour rassembler l’information et vos notes préparatoires avant une rencontre avec un avocat. Il ne s’agit pas d’un testament final ni juridiquement valide.",
    important: "Important :",
    legalImportantBody:
      "Les documents finaux peuvent exiger des formalités juridiques provinciales, une révision, une signature, des témoins et d’autres étapes professionnelles.",
    legalChecklist: [
      "AfterMe.ca ne fournit pas de conseils juridiques.",
      "Les renseignements saisis ici servent uniquement à l’organisation et à la préparation avec un avocat.",
      "N’entrez pas de mots de passe, clés privées, phrases de récupération, numéros de compte complets, NAS ni identifiants hautement sensibles.",
      "Vous pourrez demander une révision par un avocat plus tard si ce service devient disponible.",
    ],
    legalAcknowledge:
      "Je comprends qu’il s’agit d’un outil de préparation et non d’un testament juridiquement valide.",
    savingAcknowledgement: "Enregistrement de l’acceptation…",
    continue: "Continuer",
    summaryTitle: "Résumé de préparation",
    summaryDescription:
      "Utilisez ce résumé pour voir ce qui est complet et ce qui pourrait encore nécessiter une discussion avec un avocat.",
    complete: "Complet",
    sectionsDone: "Sections complétées",
    requiredGaps: "Éléments requis manquants",
    riskFlags: "Points de risque",
    completedSections: "Sections terminées",
    stillMissing: "Encore manquant",
    noRequiredGaps: "Aucun élément requis manquant pour le moment.",
    preparingSummary: "Préparation du résumé…",
    downloadSummary: "Télécharger le résumé de préparation",
    refreshing: "Actualisation…",
    refreshSummary: "Actualiser le résumé",
    lawyerReviewTitle: "Prochaine étape : révision par un avocat",
    lawyerReviewDescription:
      "Vous pouvez utiliser ce brouillon pour préparer une rencontre avec un avocat. Les honoraires sont distincts, et rien n’est finalisé avant la révision, la signature, les témoins et les exigences juridiques provinciales.",
    requestReviewHeading: "Demander une révision juridique",
    requestReviewBody:
      "Commencez une demande si vous souhaitez qu’AfterMe.ca transmette plus tard ce résumé préparatoire à un avocat ou à une équipe de révision.",
    currentStatus: "Statut actuel :",
    stillInDraft: "Toujours en brouillon",
    reviewRequestSent: "Demande de révision envoyée",
    requestLawyerReview: "Demander une révision par un avocat",
    back: "Retour",
    saveDraft: "Enregistrer le brouillon",
    saving: "Enregistrement…",
    reviewSummary: "Voir le résumé",
    loadFailed: "Impossible de charger la trousse de préparation testamentaire.",
    modalKicker: "Demande de révision juridique",
    modalTitle: "Commencer une demande de révision juridique",
    modalClose: "Fermer",
    modalLead:
      "Cela lance seulement une demande. Des frais juridiques pourraient s’appliquer plus tard, et aucune relation avocat-client n’est créée avant l’acceptation par l’avocat ou le fournisseur de service.",
    modalConsent:
      "J’autorise AfterMe.ca à partager ce résumé préparatoire avec l’avocat ou l’équipe de révision.",
    modalMessageLabel: "Message facultatif pour l’équipe de révision",
    modalMessagePlaceholder:
      "Exemple : j’aimerais de l’aide pour transformer ce brouillon en liste de vérification prête pour un avocat.",
    modalNotNow: "Pas maintenant",
    modalSending: "Envoi de la demande…",
    modalStart: "Commencer la demande de révision",
  },
};

const PROVINCES = [
  "Alberta",
  "British Columbia",
  "Manitoba",
  "New Brunswick",
  "Newfoundland and Labrador",
  "Northwest Territories",
  "Nova Scotia",
  "Nunavut",
  "Ontario",
  "Prince Edward Island",
  "Quebec",
  "Saskatchewan",
  "Yukon",
];

const STEP_META = {
  legalNotice: {
    title: "Legal notice",
    eyebrow: "Before you begin",
    description:
      "This toolkit helps you organize information for a future lawyer conversation. It does not create a legally valid will.",
  },
  personalContext: {
    title: "Personal and family context",
    eyebrow: "Step 1",
    description:
      "Capture the household context a lawyer would ask for first, without entering government ID numbers or private credentials.",
  },
  executors: {
    title: "Executor preferences",
    eyebrow: "Step 2",
    description:
      "Name the people you would like a lawyer to consider for executor roles, and note whether you have already discussed it with them.",
  },
  guardianship: {
    title: "Guardianship notes",
    eyebrow: "Step 3",
    description:
      "If minor children or dependents are part of your plan, capture high-level care notes for lawyer review.",
  },
  assets: {
    title: "Assets overview",
    eyebrow: "Step 4",
    description:
      "List broad asset categories and enough context for a lawyer to identify them later. Do not enter full account numbers or private keys.",
  },
  liabilities: {
    title: "Liabilities overview",
    eyebrow: "Step 5",
    description:
      "Summarize debts and obligations so a lawyer can see the broader picture without needing exact loan or card numbers.",
  },
  digitalLegacy: {
    title: "Digital legacy",
    eyebrow: "Step 6",
    description:
      "Describe online accounts and access plans in plain language. Keep passwords, seed phrases, and backup codes stored separately.",
  },
  finalWishes: {
    title: "Final wishes",
    eyebrow: "Step 7",
    description:
      "Capture practical and personal preferences for family discussions. These notes are not legally guaranteed instructions.",
  },
  poaProperty: {
    title: "Power of Attorney for Property",
    eyebrow: "Step 8",
    description:
      "Outline who you would want handling property or financial decisions if needed. Province-specific legal wording can vary.",
  },
  poaPersonalCare: {
    title: "Power of Attorney for Personal Care",
    eyebrow: "Step 9",
    description:
      "Outline who should know your personal care and health-related wishes. A lawyer can adapt this to your province’s terminology.",
  },
  organDonation: {
    title: "Organ donation and healthcare preferences",
    eyebrow: "Step 10",
    description:
      "Record care preferences and donation notes for family conversations. This does not replace provincial registration or formal documents.",
  },
  review: {
    title: "Review and summary",
    eyebrow: "Final step",
    description:
      "See what is complete, what may still need attention, and whether you are ready to request lawyer review.",
  },
};

const MARITAL_STATUS_OPTIONS = [
  { value: "", label: "Select one" },
  { value: "single", label: "Single" },
  { value: "married", label: "Married" },
  { value: "common-law", label: "Common-law" },
  { value: "separated", label: "Separated" },
  { value: "divorced", label: "Divorced" },
  { value: "widowed", label: "Widowed" },
  { value: "prefer-not-to-say", label: "Prefer not to say" },
];

const ASSET_OPTIONS = [
  ["real-estate", "Real estate / property"],
  ["bank-accounts", "Bank accounts"],
  ["investments", "Investments"],
  ["registered-investments", "RRSP / RRIF / TFSA / RESP / non-registered investments"],
  ["life-insurance", "Life insurance"],
  ["pension-benefits", "Employer benefits / pension"],
  ["vehicles", "Vehicles"],
  ["business-ownership", "Business ownership"],
  ["crypto-digital-assets", "Crypto / digital assets"],
  ["precious-metals", "Precious metals / commodities"],
  ["jewelry-collectibles", "Jewelry / collectibles"],
  ["personal-belongings", "Personal belongings"],
  ["other-assets", "Other assets"],
];

const LIABILITY_OPTIONS = [
  ["mortgage", "Mortgage"],
  ["heloc", "HELOC"],
  ["personal-loan", "Personal loans"],
  ["credit-card", "Credit cards"],
  ["car-loan-lease", "Car loans / leases"],
  ["business-debt", "Business debt"],
  ["tax-obligation", "Tax obligations"],
  ["other-liability", "Other liabilities"],
];

const DIGITAL_OPTIONS = [
  ["email", "Email accounts"],
  ["cloud-storage", "Cloud storage"],
  ["social-media", "Social media"],
  ["domains-websites", "Domain names / websites"],
  ["subscriptions", "Subscription accounts"],
  ["photos-videos", "Photos / video storage"],
  ["online-business", "Online business accounts"],
  ["crypto-exchange", "Crypto exchange names"],
  ["other-digital", "Other digital accounts"],
];

const OWNERSHIP_OPTIONS = [
  ["unknown", "Unknown"],
  ["sole", "Sole"],
  ["joint", "Joint"],
  ["corporation", "Corporation"],
];

const YES_NO_UNKNOWN_OPTIONS = [
  ["unknown", "Unknown"],
  ["yes", "Yes"],
  ["no", "No"],
];

const ORGAN_OPTIONS = [
  ["", "Select one"],
  ["yes", "Yes"],
  ["no", "No"],
  ["unsure", "Unsure"],
  ["already-registered", "Already registered"],
];

const SENSITIVE_PATTERNS = [
  /\bpasswords?\b/i,
  /\bprivate\s+keys?\b/i,
  /\bseed\s+phrases?\b/i,
  /\brecovery\s+phrases?\b/i,
  /\bbackup\s+codes?\b/i,
  /\baccount\s+numbers?\b/i,
  /\bpolicy\s+numbers?\b/i,
  /\bSIN\b/i,
  /\bsocial\s+insurance\s+numbers?\b/i,
];

function findSensitiveWarnings(value) {
  const hits = new Set();
  function walk(input) {
    if (!input) {
      return;
    }
    if (typeof input === "string") {
      SENSITIVE_PATTERNS.forEach((pattern) => {
        if (pattern.test(input)) {
          hits.add("Please remove passwords, seed phrases, private keys, full account numbers, SIN details, and similar sensitive credentials.");
        }
      });
      return;
    }
    if (Array.isArray(input)) {
      input.forEach(walk);
      return;
    }
    if (typeof input === "object") {
      Object.values(input).forEach(walk);
    }
  }
  walk(value);
  return [...hits];
}

function createAssetRow() {
  return {
    id: `asset-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
    category: "real-estate",
    title: "",
    institutionProviderLocation: "",
    approximateValueRange: "",
    ownershipType: "unknown",
    beneficiaryExists: "unknown",
    notesForLawyer: "",
  };
}

function createLiabilityRow() {
  return {
    id: `liability-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
    category: "mortgage",
    lenderProvider: "",
    approximateBalanceRange: "",
    ownershipType: "unknown",
    notes: "",
  };
}

function createDigitalRow() {
  return {
    id: `digital-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
    category: "email",
    name: "",
    locationOrProvider: "",
    instructions: "",
  };
}

function downloadTextFile(filename, text) {
  const blob = new Blob([text], { type: "text/plain;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement("a");
  anchor.href = url;
  anchor.download = filename;
  anchor.click();
  URL.revokeObjectURL(url);
}

function StepButton({ active, done, disabled = false, label, stepNumber, onClick }) {
  return (
    <button
      type="button"
      className={`will-stepper-btn${active ? " will-stepper-btn-active" : ""}${done ? " will-stepper-btn-done" : ""}`}
      onClick={onClick}
      disabled={disabled}
    >
      <span className="will-stepper-badge">{done ? "✓" : stepNumber}</span>
      <span>{label}</span>
    </button>
  );
}

function SectionCard({ title, description, children }) {
  return (
    <section className="will-section-card">
      <header className="will-section-card-head">
        <h3>{title}</h3>
        {description ? <p>{description}</p> : null}
      </header>
      {children}
    </section>
  );
}

function FieldRow({ children, compact = false }) {
  return <div className={`will-field-row${compact ? " will-field-row-compact" : ""}`}>{children}</div>;
}

function TextInput({ label, value, onChange, placeholder, type = "text" }) {
  return (
    <label className="will-field">
      <span>{label}</span>
      <input type={type} value={value || ""} placeholder={placeholder || ""} onChange={onChange} />
    </label>
  );
}

function TextArea({ label, value, onChange, placeholder, rows = 4, hint = "" }) {
  return (
    <label className="will-field">
      <span>{label}</span>
      <textarea rows={rows} value={value || ""} placeholder={placeholder || ""} onChange={onChange} />
      {hint ? <small>{hint}</small> : null}
    </label>
  );
}

function SelectField({ label, value, onChange, options }) {
  return (
    <label className="will-field">
      <span>{label}</span>
      <select value={value || ""} onChange={onChange}>
        {options.map(([optionValue, optionLabel]) => (
          <option key={optionValue} value={optionValue}>
            {optionLabel}
          </option>
        ))}
      </select>
    </label>
  );
}

function ReviewRequestModal({
  open,
  onClose,
  onConfirm,
  loading,
  consent,
  setConsent,
  notes,
  setNotes,
  copy,
}) {
  if (!open) {
    return null;
  }
  return (
    <div className="will-modal-backdrop" role="presentation" onClick={onClose}>
      <div
        className="will-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="will-review-request-title"
        onClick={(event) => event.stopPropagation()}
        >
        <div className="will-modal-head">
          <div>
            <p className="will-modal-kicker">{copy.modalKicker}</p>
            <h3 id="will-review-request-title">{copy.modalTitle}</h3>
          </div>
          <button type="button" className="will-modal-close" onClick={onClose} aria-label={copy.modalClose}>
            ×
          </button>
        </div>
        <p className="will-modal-lead">{copy.modalLead}</p>
        <label className="will-checkbox-card">
          <input type="checkbox" checked={consent} onChange={(event) => setConsent(event.target.checked)} />
          <span>{copy.modalConsent}</span>
        </label>
        <TextArea
          label={copy.modalMessageLabel}
          value={notes}
          onChange={(event) => setNotes(event.target.value)}
          placeholder={copy.modalMessagePlaceholder}
          rows={4}
        />
        <div className="will-modal-actions">
          <button type="button" className="secondary" onClick={onClose}>
            {copy.modalNotNow}
          </button>
          <button type="button" onClick={onConfirm} disabled={!consent || loading}>
            {loading ? copy.modalSending : copy.modalStart}
          </button>
        </div>
      </div>
    </div>
  );
}

function buildStepSequence(draft) {
  const steps = ["legalNotice", "personalContext", "executors"];
  if (draft?.personalContext?.hasMinorDependents) {
    steps.push("guardianship");
  }
  steps.push(
    "assets",
    "liabilities",
    "digitalLegacy",
    "finalWishes",
    "poaProperty",
    "poaPersonalCare",
    "organDonation",
    "review"
  );
  return steps;
}

export function WillPreparationPanel({ authToken, locale = "en" }) {
  const copy = WILL_PREP_COPY[locale === "fr" ? "fr" : "en"];
  const [draft, setDraft] = useState(null);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [noticeChecked, setNoticeChecked] = useState(false);
  const [activeStep, setActiveStep] = useState("legalNotice");
  const [dirtySection, setDirtySection] = useState("");
  const [summaryLoading, setSummaryLoading] = useState(false);
  const [reviewModalOpen, setReviewModalOpen] = useState(false);
  const [reviewConsent, setReviewConsent] = useState(false);
  const [reviewNotes, setReviewNotes] = useState("");

  const loadDraft = useCallback(async () => {
    if (!authToken) {
      return;
    }
    setLoading(true);
    setError("");
    try {
      const res = await fetch(`${API_BASE}/api/will-preparation`, {
        headers: { Authorization: `Bearer ${authToken}` },
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        throw new Error(data.message || "Could not load the will preparation draft.");
      }
      let nextDraft = data.draft || null;
      let nextSummary = data.summary || null;
      if (!nextDraft) {
        const createRes = await fetch(`${API_BASE}/api/will-preparation`, {
          method: "POST",
          headers: {
            Authorization: `Bearer ${authToken}`,
            "Content-Type": "application/json",
          },
        });
        const created = await createRes.json().catch(() => ({}));
        if (!createRes.ok) {
          throw new Error(created.message || "Could not create a will preparation draft.");
        }
        nextDraft = created.draft;
        nextSummary = created.summary;
      }
      setDraft(nextDraft);
      setSummary(nextSummary);
      setActiveStep(nextDraft?.legalNoticeAccepted ? "personalContext" : "legalNotice");
      setNoticeChecked(Boolean(nextDraft?.legalNoticeAccepted));
    } catch (err) {
      setError(err.message || "Could not load the will preparation toolkit.");
    } finally {
      setLoading(false);
    }
  }, [authToken]);

  useEffect(() => {
    loadDraft();
  }, [loadDraft]);

  const steps = useMemo(() => buildStepSequence(draft), [draft]);

  useEffect(() => {
    if (!steps.includes(activeStep)) {
      setActiveStep(steps[0] || "legalNotice");
    }
  }, [activeStep, steps]);

  const activeStepIndex = Math.max(steps.indexOf(activeStep), 0);
  const currentSectionKey = activeStep === "review" || activeStep === "legalNotice" ? "" : activeStep;
  const currentWarnings = useMemo(() => {
    if (!draft || !currentSectionKey) {
      return [];
    }
    return findSensitiveWarnings(draft[currentSectionKey]);
  }, [draft, currentSectionKey]);

  const updateSection = useCallback((sectionKey, updater) => {
    setDraft((prev) => {
      if (!prev) {
        return prev;
      }
      const current = prev[sectionKey];
      const nextValue = typeof updater === "function" ? updater(current) : updater;
      return { ...prev, [sectionKey]: nextValue };
    });
    setDirtySection(sectionKey);
  }, []);

  const saveSection = useCallback(
    async (sectionKey) => {
      if (!authToken || !draft || !sectionKey) {
        return;
      }
      setSaving(true);
      setError("");
      try {
        const res = await fetch(`${API_BASE}/api/will-preparation`, {
          method: "PATCH",
          headers: {
            Authorization: `Bearer ${authToken}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            section: sectionKey,
            value: draft[sectionKey],
          }),
        });
        const data = await res.json().catch(() => ({}));
        if (!res.ok) {
          throw new Error(data.message || "Could not save this draft section.");
        }
        setDraft(data.draft);
        setSummary(data.summary);
        setDirtySection("");
      } catch (err) {
        setError(err.message || "Could not save this draft section.");
        throw err;
      } finally {
        setSaving(false);
      }
    },
    [authToken, draft]
  );

  const goToNextStep = useCallback(async () => {
    if (!draft) {
      return;
    }
    if (currentSectionKey && dirtySection === currentSectionKey) {
      await saveSection(currentSectionKey);
    }
    const nextStep = steps[Math.min(activeStepIndex + 1, steps.length - 1)];
    setActiveStep(nextStep);
  }, [activeStepIndex, currentSectionKey, dirtySection, draft, saveSection, steps]);

  const goToPreviousStep = useCallback(() => {
    const prevStep = steps[Math.max(activeStepIndex - 1, 0)];
    setActiveStep(prevStep);
  }, [activeStepIndex, steps]);

  const acceptNotice = useCallback(async () => {
    if (!authToken || !noticeChecked) {
      return;
    }
    setSaving(true);
    setError("");
    try {
      const res = await fetch(`${API_BASE}/api/will-preparation/legal-notice/accept`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${authToken}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ accepted: true }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        throw new Error(data.message || "Could not store your legal notice acknowledgement.");
      }
      setDraft(data.draft);
      setSummary(data.summary);
      setDirtySection("");
      setActiveStep("personalContext");
    } catch (err) {
      setError(err.message || "Could not store your legal notice acknowledgement.");
    } finally {
      setSaving(false);
    }
  }, [authToken, noticeChecked]);

  const refreshSummary = useCallback(async () => {
    if (!authToken) {
      return;
    }
    setSummaryLoading(true);
    try {
      const res = await fetch(`${API_BASE}/api/will-preparation/summary`, {
        headers: { Authorization: `Bearer ${authToken}` },
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        throw new Error(data.message || "Could not refresh the summary.");
      }
      setSummary(data.summary);
      return data;
    } catch (err) {
      setError(err.message || "Could not refresh the summary.");
      return null;
    } finally {
      setSummaryLoading(false);
    }
  }, [authToken]);

  const downloadSummary = useCallback(async () => {
    const data = await refreshSummary();
    if (data?.downloadText) {
      downloadTextFile("afterme-will-preparation-summary.txt", data.downloadText);
    }
  }, [refreshSummary]);

  const submitLawyerReviewRequest = useCallback(async () => {
    if (!authToken) {
      return;
    }
    setSaving(true);
    setError("");
    try {
      const res = await fetch(`${API_BASE}/api/will-preparation/request-lawyer-review`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${authToken}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          consentToShareSummary: reviewConsent,
          reviewRequestNotes: reviewNotes,
        }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        throw new Error(data.message || "Could not start the lawyer review request.");
      }
      setDraft(data.draft);
      setSummary(data.summary);
      setReviewModalOpen(false);
      setReviewNotes("");
      setReviewConsent(false);
    } catch (err) {
      setError(err.message || "Could not start the lawyer review request.");
    } finally {
      setSaving(false);
    }
  }, [authToken, reviewConsent, reviewNotes]);

  const canRequestReview = Boolean(
    summary &&
      summary.missingRequired &&
      summary.missingRequired.length === 0 &&
      draft?.status !== "REVIEW_REQUESTED"
  );

  if (loading) {
    return (
      <div className="will-prep-page-stack">
        <section className="panel will-prep-panel">
          <p className="helper-text" role="status">
            Loading the Will Preparation Toolkit…
          </p>
        </section>
      </div>
    );
  }

  if (!draft) {
    return (
      <div className="will-prep-page-stack">
        <section className="panel will-prep-panel">
          <p className="helper-text">{error || copy.loadFailed}</p>
        </section>
      </div>
    );
  }

  return (
    <div className="will-prep-page-stack">
      <section className="panel will-prep-panel">
        <div className="will-prep-panel-bar" aria-label={copy.contextAria}>
          <div className="will-prep-panel-bar-copy">
            <p className="will-prep-kicker">{copy.planningDocuments}</p>
            <h2>{copy.title}</h2>
          </div>
          <span className="will-prep-panel-note">
            {copy.disclaimerTitle}: {copy.disclaimerPoints[0]}
          </span>
        </div>

        <div className="will-prep-progress">
          <div>
            <strong>{summary?.completionPercentage || 0}% complete</strong>
            <p>
              {draft.status === "REVIEW_REQUESTED"
                ? copy.reviewRequested
                : draft.status === "READY_FOR_REVIEW"
                  ? copy.readyForReview
                  : copy.draftInProgress}
            </p>
          </div>
          <div className="will-prep-progress-bar" aria-hidden>
            <span style={{ width: `${summary?.completionPercentage || 0}%` }} />
          </div>
        </div>

        <nav className="will-stepper" aria-label={copy.stepperAria}>
          {steps.map((stepKey, index) => (
            <StepButton
              key={stepKey}
              active={activeStep === stepKey}
              done={Boolean(summary?.sectionsCompleted?.some((item) => item.key === stepKey))}
              disabled={!draft.legalNoticeAccepted && stepKey !== "legalNotice"}
              label={STEP_META[stepKey].title}
              stepNumber={index + 1}
              onClick={() => setActiveStep(stepKey)}
            />
          ))}
        </nav>

        {error ? <p className="will-error-banner">{error}</p> : null}

        {activeStep === "legalNotice" ? (
          <div className="will-step-layout">
            <SectionCard
              title={copy.legalNoticeTitle}
              description={copy.legalNoticeDescription}
            >
              <div className="will-notice-list">
                <div className="will-callout-card">
                  <strong>{copy.important}</strong> {copy.legalImportantBody}
                </div>
                <ul>
                  {copy.legalChecklist.map((point) => (
                    <li key={point}>{point}</li>
                  ))}
                </ul>
              </div>
              <label className="will-checkbox-card">
                <input type="checkbox" checked={noticeChecked} onChange={(event) => setNoticeChecked(event.target.checked)} />
                <span>{copy.legalAcknowledge}</span>
              </label>
              <div className="will-actions">
                <button type="button" onClick={acceptNotice} disabled={!noticeChecked || saving}>
                  {saving ? copy.savingAcknowledgement : copy.continue}
                </button>
              </div>
            </SectionCard>
          </div>
        ) : null}

        {activeStep !== "legalNotice" ? (
          <div className="will-step-layout">
            <div className="will-step-main">
              <div className="will-step-head">
                <div>
                  <p className="will-prep-kicker">{STEP_META[activeStep].eyebrow}</p>
                  <h3>{STEP_META[activeStep].title}</h3>
                  <p>{STEP_META[activeStep].description}</p>
                </div>
                <div className="will-step-status-card">
                  <strong>{activeStepIndex + 1} of {steps.length}</strong>
                  <p>{dirtySection === currentSectionKey ? "Unsaved changes" : "Saved to your draft"}</p>
                </div>
              </div>

              {currentWarnings.length > 0 ? (
                <div className="will-warning-banner" role="status">
                  {currentWarnings.map((warning) => (
                    <p key={warning}>{warning}</p>
                  ))}
                </div>
              ) : null}

              {activeStep === "personalContext" ? (
                <>
                  <SectionCard
                    title="Personal details"
                    description="Provide only the context a lawyer needs to identify you and your household later."
                  >
                    <FieldRow>
                      <TextInput
                        label="Legal name"
                        value={draft.personalContext.legalName}
                        onChange={(event) =>
                          updateSection("personalContext", (current) => ({
                            ...current,
                            legalName: event.target.value,
                          }))
                        }
                        placeholder="Example: Jordan Avery Singh"
                      />
                      <TextInput
                        label="Preferred name"
                        value={draft.personalContext.preferredName}
                        onChange={(event) =>
                          updateSection("personalContext", (current) => ({
                            ...current,
                            preferredName: event.target.value,
                          }))
                        }
                        placeholder="Optional name or nickname"
                      />
                    </FieldRow>
                    <FieldRow>
                      <label className="will-field">
                        <span>Province / territory of residence</span>
                        <select
                          value={draft.personalContext.province}
                          onChange={(event) =>
                            updateSection("personalContext", (current) => ({
                              ...current,
                              province: event.target.value,
                            }))
                          }
                        >
                          <option value="">Select your province / territory</option>
                          {PROVINCES.map((province) => (
                            <option key={province} value={province}>
                              {province}
                            </option>
                          ))}
                        </select>
                      </label>
                      <label className="will-field">
                        <span>Marital status</span>
                        <select
                          value={draft.personalContext.maritalStatus}
                          onChange={(event) =>
                            updateSection("personalContext", (current) => ({
                              ...current,
                              maritalStatus: event.target.value,
                            }))
                          }
                        >
                          {MARITAL_STATUS_OPTIONS.map((option) => (
                            <option key={option.value} value={option.value}>
                              {option.label}
                            </option>
                          ))}
                        </select>
                      </label>
                    </FieldRow>
                    <FieldRow>
                      <TextInput
                        label="Spouse / partner name"
                        value={draft.personalContext.spousePartnerName}
                        onChange={(event) =>
                          updateSection("personalContext", (current) => ({
                            ...current,
                            spousePartnerName: event.target.value,
                          }))
                        }
                        placeholder="Optional"
                      />
                    </FieldRow>
                    <FieldRow>
                      <TextArea
                        label="Children / dependents summary"
                        value={draft.personalContext.dependentsSummary}
                        onChange={(event) =>
                          updateSection("personalContext", (current) => ({
                            ...current,
                            dependentsSummary: event.target.value,
                          }))
                        }
                        placeholder="Example: Two children in elementary school and one adult dependent with ongoing support needs."
                        rows={4}
                      />
                    </FieldRow>
                    <label className="will-checkbox-card">
                      <input
                        type="checkbox"
                        checked={Boolean(draft.personalContext.hasMinorDependents)}
                        onChange={(event) =>
                          updateSection("personalContext", (current) => ({
                            ...current,
                            hasMinorDependents: event.target.checked,
                          }))
                        }
                      />
                      <span>I have minor children or dependents whose care should be discussed with a lawyer.</span>
                    </label>
                    <TextArea
                      label="Notes for lawyer"
                      value={draft.personalContext.lawyerNotes}
                      onChange={(event) =>
                        updateSection("personalContext", (current) => ({
                          ...current,
                          lawyerNotes: event.target.value,
                        }))
                      }
                      placeholder="Anything a lawyer should understand about your family context."
                    />
                  </SectionCard>
                </>
              ) : null}

              {activeStep === "executors" ? (
                <>
                  <SectionCard
                    title="Preferred executor"
                    description="Executor selection should be reviewed with a lawyer before anything is finalized."
                  >
                    <FieldRow>
                      <TextInput
                        label="Preferred executor name"
                        value={draft.executors.primary.name}
                        onChange={(event) =>
                          updateSection("executors", (current) => ({
                            ...current,
                            primary: { ...current.primary, name: event.target.value },
                          }))
                        }
                        placeholder="Example: Priya Shah"
                      />
                      <TextInput
                        label="Relationship"
                        value={draft.executors.primary.relationship}
                        onChange={(event) =>
                          updateSection("executors", (current) => ({
                            ...current,
                            primary: { ...current.primary, relationship: event.target.value },
                          }))
                        }
                        placeholder="Example: Sister"
                      />
                    </FieldRow>
                    <FieldRow>
                      <TextInput
                        label="Contact email (optional)"
                        type="email"
                        value={draft.executors.primary.email}
                        onChange={(event) =>
                          updateSection("executors", (current) => ({
                            ...current,
                            primary: { ...current.primary, email: event.target.value },
                          }))
                        }
                        placeholder="Optional"
                      />
                      <TextInput
                        label="Contact phone (optional)"
                        value={draft.executors.primary.phone}
                        onChange={(event) =>
                          updateSection("executors", (current) => ({
                            ...current,
                            primary: { ...current.primary, phone: event.target.value },
                          }))
                        }
                        placeholder="Optional"
                      />
                    </FieldRow>
                    <label className="will-checkbox-card">
                      <input
                        type="checkbox"
                        checked={Boolean(draft.executors.primary.hasBeenInformed)}
                        onChange={(event) =>
                          updateSection("executors", (current) => ({
                            ...current,
                            primary: { ...current.primary, hasBeenInformed: event.target.checked },
                          }))
                        }
                      />
                      <span>This person has already been informed that I may want them in this role.</span>
                    </label>
                  </SectionCard>

                  <SectionCard title="Alternate executor">
                    <FieldRow>
                      <TextInput
                        label="Alternate executor name"
                        value={draft.executors.alternate.name}
                        onChange={(event) =>
                          updateSection("executors", (current) => ({
                            ...current,
                            alternate: { ...current.alternate, name: event.target.value },
                          }))
                        }
                        placeholder="Optional backup person"
                      />
                      <TextInput
                        label="Relationship"
                        value={draft.executors.alternate.relationship}
                        onChange={(event) =>
                          updateSection("executors", (current) => ({
                            ...current,
                            alternate: { ...current.alternate, relationship: event.target.value },
                          }))
                        }
                        placeholder="Optional"
                      />
                    </FieldRow>
                    <FieldRow>
                      <TextInput
                        label="Contact email (optional)"
                        type="email"
                        value={draft.executors.alternate.email}
                        onChange={(event) =>
                          updateSection("executors", (current) => ({
                            ...current,
                            alternate: { ...current.alternate, email: event.target.value },
                          }))
                        }
                      />
                      <TextInput
                        label="Contact phone (optional)"
                        value={draft.executors.alternate.phone}
                        onChange={(event) =>
                          updateSection("executors", (current) => ({
                            ...current,
                            alternate: { ...current.alternate, phone: event.target.value },
                          }))
                        }
                      />
                    </FieldRow>
                    <label className="will-checkbox-card">
                      <input
                        type="checkbox"
                        checked={Boolean(draft.executors.alternate.hasBeenInformed)}
                        onChange={(event) =>
                          updateSection("executors", (current) => ({
                            ...current,
                            alternate: { ...current.alternate, hasBeenInformed: event.target.checked },
                          }))
                        }
                      />
                      <span>I have already discussed this backup role with them.</span>
                    </label>
                    <TextArea
                      label="Notes for lawyer"
                      value={draft.executors.lawyerNotes}
                      onChange={(event) =>
                        updateSection("executors", (current) => ({
                          ...current,
                          lawyerNotes: event.target.value,
                        }))
                      }
                      placeholder="Context such as location, family dynamics, or why this person matters."
                    />
                  </SectionCard>
                </>
              ) : null}

              {activeStep === "guardianship" ? (
                <SectionCard
                  title="Guardianship planning notes"
                  description="Guardianship decisions may require court or legal review depending on your province or territory."
                >
                  <FieldRow>
                    <TextInput
                      label="Preferred guardian"
                      value={draft.guardianship.preferredGuardian}
                      onChange={(event) =>
                        updateSection("guardianship", (current) => ({
                          ...current,
                          preferredGuardian: event.target.value,
                        }))
                      }
                    />
                    <TextInput
                      label="Alternate guardian"
                      value={draft.guardianship.alternateGuardian}
                      onChange={(event) =>
                        updateSection("guardianship", (current) => ({
                          ...current,
                          alternateGuardian: event.target.value,
                        }))
                      }
                    />
                  </FieldRow>
                  <FieldRow>
                    <TextInput
                      label="Relationship"
                      value={draft.guardianship.relationship}
                      onChange={(event) =>
                        updateSection("guardianship", (current) => ({
                          ...current,
                          relationship: event.target.value,
                        }))
                      }
                    />
                  </FieldRow>
                  <TextArea
                    label="Important care notes"
                    value={draft.guardianship.careNotes}
                    onChange={(event) =>
                      updateSection("guardianship", (current) => ({
                        ...current,
                        careNotes: event.target.value,
                      }))
                    }
                    placeholder="Daily care, routines, health support, or family circumstances to discuss."
                  />
                  <TextArea
                    label="Education / cultural / religious / personal values notes"
                    value={draft.guardianship.valuesNotes}
                    onChange={(event) =>
                      updateSection("guardianship", (current) => ({
                        ...current,
                        valuesNotes: event.target.value,
                      }))
                    }
                    placeholder="Values or traditions that matter to your family."
                  />
                  <TextArea
                    label="Concerns to discuss with lawyer"
                    value={draft.guardianship.concernsForLawyer}
                    onChange={(event) =>
                      updateSection("guardianship", (current) => ({
                        ...current,
                        concernsForLawyer: event.target.value,
                      }))
                    }
                    placeholder="Anything that may need extra legal discussion or care."
                  />
                </SectionCard>
              ) : null}

              {activeStep === "assets" ? (
                <>
                  <SectionCard
                    title="Assets snapshot"
                    description="Provide enough detail for a lawyer to identify each asset later. Avoid full account numbers, policy numbers, passwords, or wallet keys."
                  >
                    <div className="will-inline-guidance">
                      <span>Example: TD chequing account — do not enter full account number.</span>
                      <span>Example: Life insurance with Manulife — do not enter policy number unless instructed by your lawyer.</span>
                    </div>
                  </SectionCard>
                  {draft.assets.map((asset, index) => (
                    <SectionCard key={asset.id} title={`Asset ${index + 1}`}>
                      <FieldRow>
                        <SelectField
                          label="Category"
                          value={asset.category}
                          onChange={(event) =>
                            updateSection("assets", (current) =>
                              current.map((row) =>
                                row.id === asset.id ? { ...row, category: event.target.value } : row
                              )
                            )
                          }
                          options={ASSET_OPTIONS}
                        />
                        <TextInput
                          label="Friendly name / title"
                          value={asset.title}
                          onChange={(event) =>
                            updateSection("assets", (current) =>
                              current.map((row) =>
                                row.id === asset.id ? { ...row, title: event.target.value } : row
                              )
                            )
                          }
                          placeholder="Example: Family cottage in Muskoka"
                        />
                      </FieldRow>
                      <FieldRow>
                        <TextInput
                          label="Institution / provider / location"
                          value={asset.institutionProviderLocation}
                          onChange={(event) =>
                            updateSection("assets", (current) =>
                              current.map((row) =>
                                row.id === asset.id
                                  ? { ...row, institutionProviderLocation: event.target.value }
                                  : row
                              )
                            )
                          }
                          placeholder="Optional identifying context"
                        />
                        <TextInput
                          label="Approximate value range"
                          value={asset.approximateValueRange}
                          onChange={(event) =>
                            updateSection("assets", (current) =>
                              current.map((row) =>
                                row.id === asset.id
                                  ? { ...row, approximateValueRange: event.target.value }
                                  : row
                              )
                            )
                          }
                          placeholder="Optional range only"
                        />
                      </FieldRow>
                      <FieldRow compact>
                        <SelectField
                          label="Ownership type"
                          value={asset.ownershipType}
                          onChange={(event) =>
                            updateSection("assets", (current) =>
                              current.map((row) =>
                                row.id === asset.id ? { ...row, ownershipType: event.target.value } : row
                              )
                            )
                          }
                          options={OWNERSHIP_OPTIONS}
                        />
                        <SelectField
                          label="Beneficiary exists?"
                          value={asset.beneficiaryExists}
                          onChange={(event) =>
                            updateSection("assets", (current) =>
                              current.map((row) =>
                                row.id === asset.id
                                  ? { ...row, beneficiaryExists: event.target.value }
                                  : row
                              )
                            )
                          }
                          options={YES_NO_UNKNOWN_OPTIONS}
                        />
                      </FieldRow>
                      <TextArea
                        label="Notes for lawyer"
                        value={asset.notesForLawyer}
                        onChange={(event) =>
                          updateSection("assets", (current) =>
                            current.map((row) =>
                              row.id === asset.id ? { ...row, notesForLawyer: event.target.value } : row
                            )
                          )
                        }
                        placeholder="Anything that would help a lawyer identify or discuss this asset later."
                      />
                      <div className="will-inline-actions">
                        <button
                          type="button"
                          className="secondary"
                          onClick={() =>
                            updateSection("assets", (current) => current.filter((row) => row.id !== asset.id))
                          }
                        >
                          Remove asset
                        </button>
                      </div>
                    </SectionCard>
                  ))}
                  <div className="will-inline-actions">
                    <button
                      type="button"
                      className="secondary"
                      onClick={() => updateSection("assets", (current) => [...current, createAssetRow()])}
                    >
                      Add asset
                    </button>
                  </div>
                </>
              ) : null}

              {activeStep === "liabilities" ? (
                <>
                  <SectionCard
                    title="Liabilities snapshot"
                    description="Use broad ranges and provider names only. Avoid entering exact card numbers, loan numbers, or other private identifiers."
                  />
                  {draft.liabilities.map((liability, index) => (
                    <SectionCard key={liability.id} title={`Liability ${index + 1}`}>
                      <FieldRow>
                        <SelectField
                          label="Type"
                          value={liability.category}
                          onChange={(event) =>
                            updateSection("liabilities", (current) =>
                              current.map((row) =>
                                row.id === liability.id ? { ...row, category: event.target.value } : row
                              )
                            )
                          }
                          options={LIABILITY_OPTIONS}
                        />
                        <TextInput
                          label="Lender / provider"
                          value={liability.lenderProvider}
                          onChange={(event) =>
                            updateSection("liabilities", (current) =>
                              current.map((row) =>
                                row.id === liability.id
                                  ? { ...row, lenderProvider: event.target.value }
                                  : row
                              )
                            )
                          }
                          placeholder="Example: RBC mortgage"
                        />
                      </FieldRow>
                      <FieldRow>
                        <TextInput
                          label="Approximate balance range"
                          value={liability.approximateBalanceRange}
                          onChange={(event) =>
                            updateSection("liabilities", (current) =>
                              current.map((row) =>
                                row.id === liability.id
                                  ? { ...row, approximateBalanceRange: event.target.value }
                                  : row
                              )
                            )
                          }
                          placeholder="Optional range only"
                        />
                        <SelectField
                          label="Sole / joint / unknown"
                          value={liability.ownershipType}
                          onChange={(event) =>
                            updateSection("liabilities", (current) =>
                              current.map((row) =>
                                row.id === liability.id
                                  ? { ...row, ownershipType: event.target.value }
                                  : row
                              )
                            )
                          }
                          options={OWNERSHIP_OPTIONS}
                        />
                      </FieldRow>
                      <TextArea
                        label="Notes"
                        value={liability.notes}
                        onChange={(event) =>
                          updateSection("liabilities", (current) =>
                            current.map((row) =>
                              row.id === liability.id ? { ...row, notes: event.target.value } : row
                            )
                          )
                        }
                        placeholder="Anything your lawyer should know about timing, co-borrowers, or context."
                      />
                      <div className="will-inline-actions">
                        <button
                          type="button"
                          className="secondary"
                          onClick={() =>
                            updateSection("liabilities", (current) =>
                              current.filter((row) => row.id !== liability.id)
                            )
                          }
                        >
                          Remove liability
                        </button>
                      </div>
                    </SectionCard>
                  ))}
                  <div className="will-inline-actions">
                    <button
                      type="button"
                      className="secondary"
                      onClick={() =>
                        updateSection("liabilities", (current) => [...current, createLiabilityRow()])
                      }
                    >
                      Add liability
                    </button>
                  </div>
                </>
              ) : null}

              {activeStep === "digitalLegacy" ? (
                <>
                  <SectionCard
                    title="Digital legacy overview"
                    description="Never enter passwords, seed phrases, OTP backup codes, or private keys here."
                  >
                    <div className="will-inline-guidance">
                      <span>Good note: Stored separately in secure password manager.</span>
                      <span>Good note: Trusted contact knows location.</span>
                      <span>Good note: Lawyer to review access plan.</span>
                    </div>
                  </SectionCard>
                  {draft.digitalLegacy.items.map((item, index) => (
                    <SectionCard key={item.id} title={`Digital item ${index + 1}`}>
                      <FieldRow>
                        <SelectField
                          label="Category"
                          value={item.category}
                          onChange={(event) =>
                            updateSection("digitalLegacy", (current) => ({
                              ...current,
                              items: current.items.map((row) =>
                                row.id === item.id ? { ...row, category: event.target.value } : row
                              ),
                            }))
                          }
                          options={DIGITAL_OPTIONS}
                        />
                        <TextInput
                          label="Name / description"
                          value={item.name}
                          onChange={(event) =>
                            updateSection("digitalLegacy", (current) => ({
                              ...current,
                              items: current.items.map((row) =>
                                row.id === item.id ? { ...row, name: event.target.value } : row
                              ),
                            }))
                          }
                          placeholder="Example: Family photos on Google Photos"
                        />
                      </FieldRow>
                      <FieldRow>
                        <TextInput
                          label="Provider / location"
                          value={item.locationOrProvider}
                          onChange={(event) =>
                            updateSection("digitalLegacy", (current) => ({
                              ...current,
                              items: current.items.map((row) =>
                                row.id === item.id
                                  ? { ...row, locationOrProvider: event.target.value }
                                  : row
                              ),
                            }))
                          }
                          placeholder="Optional"
                        />
                      </FieldRow>
                      <TextArea
                        label="Instructions summary"
                        value={item.instructions}
                        onChange={(event) =>
                          updateSection("digitalLegacy", (current) => ({
                            ...current,
                            items: current.items.map((row) =>
                              row.id === item.id ? { ...row, instructions: event.target.value } : row
                            ),
                          }))
                        }
                        placeholder="Example: Access stored separately in password manager; lawyer to review transfer plan."
                      />
                      <div className="will-inline-actions">
                        <button
                          type="button"
                          className="secondary"
                          onClick={() =>
                            updateSection("digitalLegacy", (current) => ({
                              ...current,
                              items: current.items.filter((row) => row.id !== item.id),
                            }))
                          }
                        >
                          Remove item
                        </button>
                      </div>
                    </SectionCard>
                  ))}
                  <div className="will-inline-actions">
                    <button
                      type="button"
                      className="secondary"
                      onClick={() =>
                        updateSection("digitalLegacy", (current) => ({
                          ...current,
                          items: [...current.items, createDigitalRow()],
                        }))
                      }
                    >
                      Add digital item
                    </button>
                  </div>
                  <SectionCard title="Overall instructions summary">
                    <TextArea
                      label="Instructions summary"
                      value={draft.digitalLegacy.instructionsSummary}
                      onChange={(event) =>
                        updateSection("digitalLegacy", (current) => ({
                          ...current,
                          instructionsSummary: event.target.value,
                        }))
                      }
                      placeholder="Broad instructions for where access details are stored and who should help."
                    />
                    <TextArea
                      label="Notes for lawyer"
                      value={draft.digitalLegacy.lawyerNotes}
                      onChange={(event) =>
                        updateSection("digitalLegacy", (current) => ({
                          ...current,
                          lawyerNotes: event.target.value,
                        }))
                      }
                      placeholder="Any concerns about digital access or memorialization."
                    />
                  </SectionCard>
                </>
              ) : null}

              {activeStep === "finalWishes" ? (
                <SectionCard
                  title="Final wishes and preference notes"
                  description="These are preference notes, not legally guaranteed instructions."
                >
                  <FieldRow>
                    <TextInput
                      label="Funeral / burial / cremation preference"
                      value={draft.finalWishes.funeralPreference}
                      onChange={(event) =>
                        updateSection("finalWishes", (current) => ({
                          ...current,
                          funeralPreference: event.target.value,
                        }))
                      }
                    />
                    <TextInput
                      label="Charitable giving preferences"
                      value={draft.finalWishes.charitableGiving}
                      onChange={(event) =>
                        updateSection("finalWishes", (current) => ({
                          ...current,
                          charitableGiving: event.target.value,
                        }))
                      }
                    />
                  </FieldRow>
                  <TextArea
                    label="Religious / cultural preferences"
                    value={draft.finalWishes.culturalReligiousPreferences}
                    onChange={(event) =>
                      updateSection("finalWishes", (current) => ({
                        ...current,
                        culturalReligiousPreferences: event.target.value,
                      }))
                    }
                  />
                  <TextArea
                    label="Memorial instructions"
                    value={draft.finalWishes.memorialInstructions}
                    onChange={(event) =>
                      updateSection("finalWishes", (current) => ({
                        ...current,
                        memorialInstructions: event.target.value,
                      }))
                    }
                  />
                  <TextArea
                    label="Messages or wishes for family"
                    value={draft.finalWishes.familyMessages}
                    onChange={(event) =>
                      updateSection("finalWishes", (current) => ({
                        ...current,
                        familyMessages: event.target.value,
                      }))
                    }
                  />
                  <FieldRow>
                    <TextArea
                      label="Pet care wishes"
                      value={draft.finalWishes.petCare}
                      onChange={(event) =>
                        updateSection("finalWishes", (current) => ({
                          ...current,
                          petCare: event.target.value,
                        }))
                      }
                    />
                    <TextArea
                      label="Special personal items distribution notes"
                      value={draft.finalWishes.specialItemsNotes}
                      onChange={(event) =>
                        updateSection("finalWishes", (current) => ({
                          ...current,
                          specialItemsNotes: event.target.value,
                        }))
                      }
                    />
                  </FieldRow>
                </SectionCard>
              ) : null}

              {activeStep === "poaProperty" ? (
                <SectionCard
                  title="Property / financial decision-maker planning"
                  description="Province-specific terminology can vary. A lawyer can help adapt these notes."
                >
                  <FieldRow>
                    <TextInput
                      label="Preferred attorney for property"
                      value={draft.poaProperty.preferredAttorney}
                      onChange={(event) =>
                        updateSection("poaProperty", (current) => ({
                          ...current,
                          preferredAttorney: event.target.value,
                        }))
                      }
                    />
                    <TextInput
                      label="Alternate attorney"
                      value={draft.poaProperty.alternateAttorney}
                      onChange={(event) =>
                        updateSection("poaProperty", (current) => ({
                          ...current,
                          alternateAttorney: event.target.value,
                        }))
                      }
                    />
                  </FieldRow>
                  <TextArea
                    label="Important contact or context notes"
                    value={draft.poaProperty.contactNotes}
                    onChange={(event) =>
                      updateSection("poaProperty", (current) => ({
                        ...current,
                        contactNotes: event.target.value,
                      }))
                    }
                  />
                  <TextArea
                    label="Notes for lawyer"
                    value={draft.poaProperty.lawyerNotes}
                    onChange={(event) =>
                      updateSection("poaProperty", (current) => ({
                        ...current,
                        lawyerNotes: event.target.value,
                      }))
                    }
                  />
                </SectionCard>
              ) : null}

              {activeStep === "poaPersonalCare" ? (
                <SectionCard
                  title="Personal care / health planning"
                  description="Capture the people and values that should guide future healthcare or care decisions."
                >
                  <FieldRow>
                    <TextInput
                      label="Preferred decision-maker"
                      value={draft.poaPersonalCare.preferredAttorney}
                      onChange={(event) =>
                        updateSection("poaPersonalCare", (current) => ({
                          ...current,
                          preferredAttorney: event.target.value,
                        }))
                      }
                    />
                    <TextInput
                      label="Alternate decision-maker"
                      value={draft.poaPersonalCare.alternateAttorney}
                      onChange={(event) =>
                        updateSection("poaPersonalCare", (current) => ({
                          ...current,
                          alternateAttorney: event.target.value,
                        }))
                      }
                    />
                  </FieldRow>
                  <TextArea
                    label="Important healthcare wishes"
                    value={draft.poaPersonalCare.healthcareWishes}
                    onChange={(event) =>
                      updateSection("poaPersonalCare", (current) => ({
                        ...current,
                        healthcareWishes: event.target.value,
                      }))
                    }
                  />
                  <TextArea
                    label="Long-term care preferences"
                    value={draft.poaPersonalCare.longTermCarePreferences}
                    onChange={(event) =>
                      updateSection("poaPersonalCare", (current) => ({
                        ...current,
                        longTermCarePreferences: event.target.value,
                      }))
                    }
                  />
                  <TextArea
                    label="Emergency contacts"
                    value={draft.poaPersonalCare.emergencyContacts}
                    onChange={(event) =>
                      updateSection("poaPersonalCare", (current) => ({
                        ...current,
                        emergencyContacts: event.target.value,
                      }))
                    }
                  />
                  <TextArea
                    label="Notes for lawyer"
                    value={draft.poaPersonalCare.lawyerNotes}
                    onChange={(event) =>
                      updateSection("poaPersonalCare", (current) => ({
                        ...current,
                        lawyerNotes: event.target.value,
                      }))
                    }
                  />
                </SectionCard>
              ) : null}

              {activeStep === "organDonation" ? (
                <SectionCard
                  title="Organ donation and healthcare preferences"
                  description="This section does not replace provincial registration or medical / legal documentation."
                >
                  <FieldRow>
                    <SelectField
                      label="Organ / tissue donation preference"
                      value={draft.organDonation.preference}
                      onChange={(event) =>
                        updateSection("organDonation", (current) => ({
                          ...current,
                          preference: event.target.value,
                          alreadyRegistered:
                            event.target.value === "already-registered" ? true : current.alreadyRegistered,
                        }))
                      }
                      options={ORGAN_OPTIONS}
                    />
                    <label className="will-checkbox-card will-checkbox-card-compact">
                      <input
                        type="checkbox"
                        checked={Boolean(draft.organDonation.alreadyRegistered)}
                        onChange={(event) =>
                          updateSection("organDonation", (current) => ({
                            ...current,
                            alreadyRegistered: event.target.checked,
                          }))
                        }
                      />
                      <span>I have already registered with my provincial organ donation registry.</span>
                    </label>
                  </FieldRow>
                  <TextArea
                    label="Healthcare values notes"
                    value={draft.organDonation.healthcareValues}
                    onChange={(event) =>
                      updateSection("organDonation", (current) => ({
                        ...current,
                        healthcareValues: event.target.value,
                      }))
                    }
                  />
                  <TextArea
                    label="End-of-life care preferences"
                    value={draft.organDonation.endOfLifePreferences}
                    onChange={(event) =>
                      updateSection("organDonation", (current) => ({
                        ...current,
                        endOfLifePreferences: event.target.value,
                      }))
                    }
                  />
                  <TextArea
                    label="Who should know these wishes"
                    value={draft.organDonation.whoShouldKnow}
                    onChange={(event) =>
                      updateSection("organDonation", (current) => ({
                        ...current,
                        whoShouldKnow: event.target.value,
                      }))
                    }
                  />
                  <TextArea
                    label="Notes for lawyer"
                    value={draft.organDonation.lawyerNotes}
                    onChange={(event) =>
                      updateSection("organDonation", (current) => ({
                        ...current,
                        lawyerNotes: event.target.value,
                      }))
                    }
                  />
                </SectionCard>
              ) : null}

              {activeStep === "review" ? (
                <>
                  <SectionCard
                    title={copy.summaryTitle}
                    description={copy.summaryDescription}
                  >
                    <div className="will-summary-grid">
                      <div className="will-summary-stat">
                        <strong>{summary?.completionPercentage || 0}%</strong>
                        <span>{copy.complete}</span>
                      </div>
                      <div className="will-summary-stat">
                        <strong>{summary?.sectionsCompleted?.length || 0}</strong>
                        <span>{copy.sectionsDone}</span>
                      </div>
                      <div className="will-summary-stat">
                        <strong>{summary?.missingRequired?.length || 0}</strong>
                        <span>{copy.requiredGaps}</span>
                      </div>
                      <div className="will-summary-stat">
                        <strong>{summary?.riskFlags?.length || 0}</strong>
                        <span>{copy.riskFlags}</span>
                      </div>
                    </div>
                    <div className="will-review-columns">
                      <div>
                        <h4>{copy.completedSections}</h4>
                        <ul className="will-list">
                          {(summary?.sectionsCompleted || []).map((item) => (
                            <li key={item.key}>{item.title}</li>
                          ))}
                        </ul>
                      </div>
                      <div>
                        <h4>{copy.stillMissing}</h4>
                        <ul className="will-list">
                          {(summary?.missingRequired || []).map((item) => (
                            <li key={item.key}>{item.title}</li>
                          ))}
                          {(summary?.missingRequired || []).length === 0 ? <li>{copy.noRequiredGaps}</li> : null}
                        </ul>
                      </div>
                    </div>
                    {(summary?.riskFlags || []).length > 0 ? (
                      <div className="will-risk-list" role="status">
                        {(summary?.riskFlags || []).map((flag) => (
                          <div key={flag.id} className={`will-risk-card will-risk-card-${flag.severity}`}>
                            {flag.label}
                          </div>
                        ))}
                      </div>
                    ) : null}
                    {(summary?.sensitiveWarnings || []).length > 0 ? (
                      <div className="will-warning-banner">
                        {summary.sensitiveWarnings.map((warning) => (
                          <p key={warning}>{warning}</p>
                        ))}
                      </div>
                    ) : null}
                    <div className="will-inline-actions">
                      <button type="button" className="secondary" onClick={downloadSummary} disabled={summaryLoading}>
                        {summaryLoading ? copy.preparingSummary : copy.downloadSummary}
                      </button>
                      <button type="button" className="secondary" onClick={refreshSummary} disabled={summaryLoading}>
                        {summaryLoading ? copy.refreshing : copy.refreshSummary}
                      </button>
                    </div>
                  </SectionCard>

                  <SectionCard
                    title={copy.lawyerReviewTitle}
                    description={copy.lawyerReviewDescription}
                  >
                    <div className="will-lawyer-review-card">
                      <div>
                        <h4>{copy.requestReviewHeading}</h4>
                        <p>{copy.requestReviewBody}</p>
                        <p className="helper-text">
                          {copy.currentStatus}{" "}
                          {draft.status === "REVIEW_REQUESTED"
                            ? copy.reviewRequested
                            : draft.status === "READY_FOR_REVIEW"
                              ? copy.readyForReview
                              : copy.stillInDraft}
                        </p>
                      </div>
                      <button type="button" onClick={() => setReviewModalOpen(true)} disabled={!canRequestReview || saving}>
                        {draft.status === "REVIEW_REQUESTED" ? copy.reviewRequestSent : copy.requestLawyerReview}
                      </button>
                    </div>
                  </SectionCard>
                </>
              ) : null}

              {activeStep !== "review" ? (
                <div className="will-actions">
                  <button
                    type="button"
                    className="secondary"
                    onClick={goToPreviousStep}
                    disabled={activeStepIndex === 0 || saving}
                  >
                    {copy.back}
                  </button>
                  <button
                    type="button"
                    className="secondary"
                    onClick={() => saveSection(currentSectionKey)}
                    disabled={!currentSectionKey || dirtySection !== currentSectionKey || saving}
                  >
                    {saving && dirtySection === currentSectionKey ? copy.saving : copy.saveDraft}
                  </button>
                  <button type="button" onClick={goToNextStep} disabled={saving}>
                    {activeStep === steps[steps.length - 2] ? copy.reviewSummary : copy.continue}
                  </button>
                </div>
              ) : null}
            </div>
          </div>
        ) : null}
      </section>

      <ReviewRequestModal
        open={reviewModalOpen}
        onClose={() => setReviewModalOpen(false)}
        onConfirm={submitLawyerReviewRequest}
        loading={saving}
        consent={reviewConsent}
        setConsent={setReviewConsent}
        notes={reviewNotes}
        setNotes={setReviewNotes}
        copy={copy}
      />
    </div>
  );
}
